import {
  ModalOptions,
  ReactComponentOrElement,
  createAnimation,
  useIonModal,
} from '@ionic/react';
import { HookOverlayOptions } from '@ionic/react/dist/types/hooks/HookOverlayOptions';
import { useCallback } from 'react';
import './modal-dialog.css';

type ModalDialogPresentOptions = Omit<
  ModalOptions,
  'component' | 'componentProps'
> &
  HookOverlayOptions;

const useModalDialog = (
  component: ReactComponentOrElement,
  componentProps?: any
) => {
  const [presentModal, dismissModal] = useIonModal(component, componentProps);

  const present = useCallback(
    (options: ModalDialogPresentOptions = {}) => {
      presentModal({
        cssClass: 'modal-dialog',
        enterAnimation,
        leaveAnimation,
        ...options,
      });
    },
    [presentModal]
  );

  return {
    present,
    dismiss: dismissModal,
  };
};

const enterAnimation = (baseEl: HTMLElement) => {
  const root = baseEl.shadowRoot as ShadowRoot;

  const backdropAnimation = createAnimation()
    .addElement(root.querySelector('ion-backdrop') as Element)
    .fromTo('opacity', '0.1', 'var(--backdrop-opacity)');

  const wrapperAnimation = createAnimation()
    .addElement(root.querySelector('.modal-wrapper') as Element)
    .beforeStyles({
      transform: 'translateY(-15vh)', // move center point up 15% from center of screen
      'transform-origin': 'top center',
      overflow: 'hidden',
    })
    .fromTo('opacity', 0, 1);

  return createAnimation()
    .easing('ease-out')
    .duration(600)
    .addAnimation([backdropAnimation, wrapperAnimation]);
};

const leaveAnimation = (baseEl: HTMLElement) => {
  const root = baseEl.shadowRoot as ShadowRoot;
  const backdropAnimation = createAnimation()
    .addElement(root.querySelector('ion-backdrop') as Element)
    .fromTo('opacity', 'var(--backdrop-opacity)', '0.01');

  const wrapperAnimation = createAnimation()
    .addElement(root.querySelector('.modal-wrapper') as Element)
    .fromTo('opacity', 1, 0);

  return (
    createAnimation()
      // .addElement(baseEl)
      .easing('ease-out')
      .duration(200)
      .addAnimation([backdropAnimation, wrapperAnimation])
  );
};

export { useModalDialog };
